'use client';

import { Link, useLocation } from 'react-router-dom';
import { Mail, MapPin, Phone, Instagram } from 'lucide-react';
import type { Locale } from '@/lib/i18n/config';
import type { Dictionary } from '@/lib/i18n/dictionaries';
import type { SiteSettings } from '@/lib/supabase/types';
import { Logo } from './logo';
import { localePath } from '@/lib/i18n/helpers';

export function Footer({
  locale,
  dict,
  settings,
}: {
  locale: Locale;
  dict: Dictionary;
  settings: SiteSettings | null;
}) {
  const base = `/${locale}`;
  const instagram = settings?.social_instagram || 'https://www.instagram.com/biometriavocalpy';
  const pathname = useLocation().pathname;
  const onLinkClick = (href: string) => () => {
    if (typeof window === 'undefined') return;
    if (pathname === href) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
      document.getElementById('main')?.focus({ preventScroll: true });
    }
  };

  return (
    <footer className="border-t border-white/10 bg-[hsl(224,52%,10%)] text-white/80">
      <div className="container-tight grid gap-10 py-16 md:grid-cols-2 lg:grid-cols-4">
        <div className="text-center md:text-left">
          <Logo inverse size="lg" dict={dict} />
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/70 mx-auto md:mx-0">
            {dict.footer.tagline}
          </p>
          <div className="mt-5 flex items-center gap-3 justify-center md:justify-start">
            <a
              href={instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 bg-white/5 text-white/60 transition hover:bg-accent hover:text-white hover:border-accent"
              aria-label="Instagram"
            >
              <Instagram className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="text-center md:text-left">
          <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-white">
            {dict.footer.quickLinks}
          </h4>
          <ul className="space-y-2 text-sm">
            <li><Link to={`${base}/areas`} onClick={onLinkClick(`${base}/areas`)} className="hover:text-white transition">{dict.nav.services}</Link></li>
            <li><Link to={`${base}/equipo`} onClick={onLinkClick(`${base}/equipo`)} className="hover:text-white transition">{dict.nav.team}</Link></li>
            <li><Link to={`${base}/noticias`} onClick={onLinkClick(`${base}/noticias`)} className="hover:text-white transition">{dict.nav.news}</Link></li>
            <li><Link to={`${base}/preguntas`} onClick={onLinkClick(`${base}/preguntas`)} className="hover:text-white transition">{dict.nav.faqs}</Link></li>
            <li><Link to={`${base}/contacto`} onClick={onLinkClick(`${base}/contacto`)} className="hover:text-white transition">{dict.nav.contact}</Link></li>
            <li><Link to={localePath('/solicitar', locale)} onClick={onLinkClick(localePath('/solicitar', locale))} className="hover:text-white transition">{dict.nav.request}</Link></li>
          </ul>
        </div>

        <div className="text-center md:text-left">
          <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-white">
            {dict.footer.standards}
          </h4>
          <ul className="space-y-3 text-sm leading-snug">
            <li>
              <span className="font-semibold text-white">IAFPA</span>
              <span className="block text-xs text-white/60">International Association for Forensic Phonetics and Acoustics</span>
            </li>
            <li>
              <span className="font-semibold text-white">ENFSI</span>
              <span className="block text-xs text-white/60">European Network of Forensic Science Institutes</span>
            </li>
            <li>
              <span className="font-semibold text-white">NIST</span>
              <span className="block text-xs text-white/60">National Institute of Standards and Technology</span>
            </li>
          </ul>
        </div>

        <div className="text-center md:text-left">
          <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-white">
            {dict.footer.contactUs}
          </h4>
          <ul className="space-y-3 text-sm">
            {settings?.address && (
              <li className="flex gap-2 justify-center md:justify-start"><MapPin className="h-4 w-4 shrink-0 text-accent" />{settings.address}</li>
            )}
            {settings?.phone && (
              <li className="flex gap-2 justify-center md:justify-start"><Phone className="h-4 w-4 shrink-0 text-accent" />{settings.phone}</li>
            )}
            {settings?.email && (
              <li className="flex gap-2 justify-center md:justify-start"><Mail className="h-4 w-4 shrink-0 text-accent" />{settings.email}</li>
            )}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-tight flex flex-col items-center justify-between gap-2 py-5 text-xs text-white/60 md:flex-row">
          <p>&copy; {new Date().getFullYear()} {settings?.organization_name ?? 'VPS Laboratorio'}. {dict.footer.rights}</p>
          <p className="uppercase tracking-wider">{settings?.years_experience} {dict.hero.stats.years}</p>
        </div>
      </div>
    </footer>
  );
}
